import _extends from "@swc/helpers/src/_extends.mjs";
import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { FormProvider, Controller, useForm } from 'react-hook-form';
import NftReduxModule from '../redux/ModuleLoadable';
import Modal, { MODAL_FOOTER_BUTTON_SIZE } from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import ModalFooterDivider from '@wix/da-ds/pkg/modals/ModalFooterDivider';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import { ListItemCheckbox } from '@wix/da-ds/pkg/List/ListItem';
import DropdownInput from './_partials/DropdownInput';
import s from './NftLinkPopup.scss';
// NOTE: Needs to be kept in sync with lib.nft on the backend
const blockchainNames = [
    'ETHEREUM',
    'POLYGON',
    'SOLANA',
    'KLAYTN',
    'ARBITRUM',
    'OPTIMISM',
    'TEZOS',
    'FLOW',
    'PALM'
];
const marketplacesByBlockchain = {
    ETHEREUM: 'OpenSea',
    POLYGON: 'OpenSea',
    SOLANA: 'OpenSea',
    KLAYTN: 'OpenSea',
    TEZOS: 'Rarible',
    FLOW: 'Rarible',
    PALM: "Nifty's",
    ARBITRUM: 'Arbazaar',
    OPTIMISM: 'Quixotic'
};
export const NftLinkPopup = ({ deviation , nft , requestClose , updateNft , deleteNft  })=>{
    var _nft_externalLinks_filter_;
    const { t  } = useTranslation();
    let initialKnownMarketplace = true, initialUnknownMarketplace = false;
    const unknownMarketplaceFilter = (link)=>link.name === 'deviation_custom_marketplace_url';
    if (nft && nft.externalLinks.length) {
        const unknownMarketplaceLinks = nft.externalLinks.filter(unknownMarketplaceFilter);
        initialUnknownMarketplace = unknownMarketplaceLinks.length > 0;
        initialKnownMarketplace = nft.externalLinks.length - unknownMarketplaceLinks.length > 0;
    }
    const formContext = useForm({
        mode: 'onChange',
        defaultValues: {
            blockchainName: (nft === null || nft === void 0 ? void 0 : nft.blockchainName) || 'ETHEREUM',
            blockchainIdentifierPrimary: (nft === null || nft === void 0 ? void 0 : nft.blockchainIdentifierPrimary) || '',
            blockchainIdentifierSecondary: (nft === null || nft === void 0 ? void 0 : nft.blockchainIdentifierSecondary) || '',
            knownMarketplace: initialKnownMarketplace,
            unknownMarketplace: initialUnknownMarketplace,
            unknownMarketplaceLink: ((_nft_externalLinks_filter_ = nft === null || nft === void 0 ? void 0 : nft.externalLinks.filter(unknownMarketplaceFilter)[0]) === null || _nft_externalLinks_filter_ === void 0 ? void 0 : _nft_externalLinks_filter_.url) || ''
        }
    });
    const { handleSubmit , setValue , formState: { errors , isValid  } , watch  } = formContext;
    const formValues = watch();
    const { blockchainName , unknownMarketplace , unknownMarketplaceLink  } = formValues;
    const renderFooterButtons = ()=>[
            nft && /*#__PURE__*/ React.createElement(TextButton, {
                size: "medium",
                variant: "destructive-red",
                key: "edit-delete",
                onClick: ()=>deleteNft(deviation, nft)
            }, t('nft.linkPopup.button.delete')),
            nft && /*#__PURE__*/ React.createElement(ModalFooterDivider, {
                key: "divider"
            }),
            /*#__PURE__*/ React.createElement(TextButton, {
                size: "medium",
                variant: "main",
                key: "cancel",
                onClick: requestClose
            }, t('nft.linkPopup.button.cancel')),
            /*#__PURE__*/ React.createElement(BrandedButton, {
                size: MODAL_FOOTER_BUTTON_SIZE,
                variant: "brand-green",
                key: "submit",
                disabled: !isValid,
                onClick: handleSubmit(()=>updateNft(deviation, formValues, nft))
            }, nft ? t('nft.linkPopup.button.update') : t('nft.linkPopup.button.create'))
        ];
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(NftReduxModule, null), /*#__PURE__*/ React.createElement(FormProvider, _extends({}, formContext), /*#__PURE__*/ React.createElement(Modal, {
        isOpen: true,
        onRequestClose: requestClose,
        showHeaderBorder: true,
        showFooterBorder: true,
        heading: nft ? t('nft.linkPopup.title.edit') : t('nft.linkPopup.title'),
        footerRight: renderFooterButtons()
    }, /*#__PURE__*/ React.createElement("div", {
        className: s['root']
    }, /*#__PURE__*/ React.createElement("p", null, t('nft.linkPopup.description')), /*#__PURE__*/ React.createElement(Controller, {
        name: "blockchainName",
        render: ({ onChange , value  })=>/*#__PURE__*/ React.createElement(DropdownInput, {
                wrapperClassName: classnames(s['form-input'], s['dropdown-item']),
                items: blockchainNames.map((name)=>{
                    return {
                        value: name,
                        label: `${name[0]}${name.slice(1).toLowerCase()}`
                    };
                }),
                id: "blockchainName",
                label: t('nft.linkPopup.input.blockchainName.label'),
                value: value,
                onSelect: onChange
            })
    }), /*#__PURE__*/ React.createElement(Controller, {
        name: "blockchainIdentifierPrimary",
        rules: {
            required: true
        },
        render: ({ onChange , value  })=>/*#__PURE__*/ React.createElement(TextInput, {
                id: "blockchainIdentifierPrimary",
                name: "blockchainIdentifierPrimary",
                details: errors.blockchainIdentifierPrimary && t('nft.linkPopup.error.blockchainIdentifierPrimary'),
                validationResult: errors.blockchainIdentifierPrimary && 'error',
                label: t('nft.linkPopup.input.blockchainIdentifierPrimary.label'),
                value: value,
                onChange: onChange,
                isRequired: true
            })
    }), blockchainName !== 'SOLANA' && /*#__PURE__*/ React.createElement(Controller, {
        name: "blockchainIdentifierSecondary",
        rules: {
            required: blockchainName !== 'SOLANA'
        },
        render: ({ onChange , value  })=>/*#__PURE__*/ React.createElement(TextInput, {
                id: "blockchainIdentifierSecondary",
                name: "blockchainIdentifierSecondary",
                details: errors.blockchainIdentifierSecondary && t('nft.linkPopup.error.blockchainIdentifierSecondary'),
                validationResult: errors.blockchainIdentifierSecondary && 'error',
                label: t('nft.linkPopup.input.blockchainIdentifierSecondary.label'),
                value: value,
                onChange: onChange,
                isRequired: true
            })
    }), /*#__PURE__*/ React.createElement(Controller, {
        name: "knownMarketplace",
        render: ({ onChange , value  })=>/*#__PURE__*/ React.createElement(ListItemCheckbox, {
                className: s['select-item'],
                isLabelBold: true,
                label: t('nft.linkPopup.input.knownMarketplace.label', {
                    marketplace: marketplacesByBlockchain[blockchainName]
                }),
                subLabel: t('nft.linkPopup.input.knownMarketplace.subLabel', {
                    marketplace: marketplacesByBlockchain[blockchainName]
                }),
                checked: !!value,
                onChange: onChange
            })
    }), /*#__PURE__*/ React.createElement(Controller, {
        name: "unknownMarketplace",
        render: ({ onChange , value  })=>/*#__PURE__*/ React.createElement(ListItemCheckbox, {
                isLabelBold: true,
                label: t('nft.linkPopup.input.unknownMarketplace.label'),
                subLabel: t('nft.linkPopup.input.unknownMarketplace.subLabel'),
                checked: !!value || !!unknownMarketplaceLink,
                onChange: onChange,
                bottomSlot: /*#__PURE__*/ React.createElement(Controller, {
                    name: "unknownMarketplaceLink",
                    rules: {
                        // Lifted a URL regex, as ain't nobody got time for that
                        required: !!unknownMarketplace,
                        pattern: /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/,
                        minLength: 7,
                        maxLength: 256
                    },
                    render: ({ onChange: onUrlChange , value: urlValue  })=>/*#__PURE__*/ React.createElement(TextInput, {
                            className: s['custom-url'],
                            id: "unknownMarketplaceLink",
                            name: "unknownMarketplaceLink",
                            details: errors.unknownMarketplaceLink && t('nft.linkPopup.error.unknownMarketplaceLink'),
                            validationResult: errors.unknownMarketplaceLink && 'error',
                            label: t('nft.linkPopup.input.unknownMarketplaceLink.label'),
                            isLabelHidden: true,
                            value: urlValue,
                            onChange: (e)=>{
                                setValue('unknownMarketplace', true);
                                onUrlChange(e);
                            }
                        })
                })
            })
    })))));
};
NftLinkPopup.displayName = 'NftLinkPopup';
export default NftLinkPopup;
